// Which discontinued cart lines offer their RBP linkages (RBP-D33, SL1.0 `get_orders_cost.php:
// 385-399`): an inactive line, core or not, whose product has a child active in the cart's
// catalogue. The review's `discontinued` is the only inactive signal the cart carries, so the page
// asks the links read for those lines alone (they are rare) and keeps each answer for the entry:
// a line links when the answer holds at least one product that is not `notAvailable`. A 404 (no
// RBP feature for this caller) is a definite "no link"; a failed read is left unknown and asked
// again on the next change of the cart's discontinued lines (or the next visit).
//
// The read is skipped where the server would answer 404 anyway, as the core marks are (WOW, Elite,
// LSN, Corrib Retail; a role outside the browse-side roles; a licence-trade session).

import { useEffect, useRef, useState } from 'react';
import { RBP_BROWSE_SIDE_ROLES } from '../auth/rbac';
import { useSessionStore } from '../auth/useSessionStore';
import { fetchRbpLinks } from '../api/rbp-links.client';
import { isRbpEntryKind } from '../api/rbp.client';

export function useRbpLinkedLines(discontinuedSkus: readonly string[]): ReadonlySet<string> {
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const role = useSessionStore((s) => s.role);
  const accountType = useSessionStore((s) => s.accountType);
  const skuKey = [...new Set(discontinuedSkus)].sort().join('\u0000');
  const shows =
    entryKey !== null &&
    isRbpEntryKind(entryKey.split(':')[0] ?? '') &&
    role !== null &&
    RBP_BROWSE_SIDE_ROLES.includes(role) &&
    accountType !== 'LicenceTrade';

  // Answers per `${entryKey}|${sku}`: true when the line links. Keyed by the entry, so an answer
  // that lands after a change of entry is never read for the new one.
  const [answers, setAnswers] = useState<Readonly<Record<string, boolean>>>({});
  const asked = useRef(new Set<string>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!shows || entryKey === null || skuKey === '') return;
    for (const sku of skuKey.split('\u0000')) {
      const key = `${entryKey}|${sku}`;
      if (asked.current.has(key)) continue;
      asked.current.add(key);
      void fetchRbpLinks(entryKey, sku).then(
        (links) => {
          const linked = links?.products.some((product) => product.notAvailable !== true) === true;
          if (mounted.current) setAnswers((previous) => ({ ...previous, [key]: linked }));
        },
        () => {
          asked.current.delete(key);
        },
      );
    }
  }, [shows, entryKey, skuKey]);

  if (!shows || entryKey === null) return new Set();
  return new Set(discontinuedSkus.filter((sku) => answers[`${entryKey}|${sku}`] === true));
}
